import React from 'react';
import type { Guild } from '../../types';

interface GuildCardProps {
  guild: Guild;
  isAvailable: boolean;
  isBotPresent: boolean;
  isUserAdmin: boolean;
  onSelect: (guildId: string) => void;
  onAddBot?: (e: React.MouseEvent) => void;
  onRemove?: (guildId: string) => void;
}

export const GuildCard: React.FC<GuildCardProps> = ({
  guild,
  isAvailable,
  isBotPresent,
  isUserAdmin,
  onSelect,
  onAddBot,
  onRemove,
}) => {
  const handleClick = () => {
    if (isAvailable) {
      onSelect(guild.id);
    }
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onRemove?.(guild.id);
  };

  return (
    <div
      onClick={handleClick}
      className={`group relative bg-surface-container-low hover:bg-surface-container transition-all duration-200 rounded-lg flex items-center p-4 ${
        isAvailable
          ? 'cursor-pointer'
          : 'opacity-75 hover:opacity-100'
      }`}
    >
      {/* Activity Status Ribbon */}
      <div
        className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
          isAvailable ? 'bg-success' : 'bg-secondary-container'
        }`}
      />

      {/* Guild Icon */}
      {guild.icon ? (
        <img
          src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
          alt={guild.name}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
        />
      ) : (
        <div className="w-12 h-12 rounded-full bg-secondary-container text-on-secondary-container flex items-center justify-center text-lg font-semibold flex-shrink-0">
          {guild.name.charAt(0).toUpperCase()}
        </div>
      )}

      {/* Guild Info */}
      <div className="ml-4 min-w-0 flex-1">
        <h3 className="text-on-surface font-semibold truncate">{guild.name}</h3>

        {isAvailable ? (
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="w-1.5 h-1.5 rounded-full bg-success inline-block" />
            <span className="text-success uppercase tracking-wider text-[10px] font-semibold">
              Active
            </span>
          </div>
        ) : !isUserAdmin ? (
          <span className="text-on-surface-variant text-xs">
            Admin access required
          </span>
        ) : !isBotPresent ? (
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-on-surface-variant text-xs">Bot not in server</span>
            <button
              onClick={onAddBot}
              className="text-[10px] font-semibold text-primary-text hover:underline uppercase tracking-wider"
            >
              Add Bot
            </button>
          </div>
        ) : null}
      </div>

      {/* Right Icons */}
      <div className="flex-shrink-0 ml-2 flex items-center gap-1">
        {onRemove && (
          <button
            onClick={handleRemoveClick}
            aria-label={`Stop tracking ${guild.name}`}
            title="Stop tracking this server"
            className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-surface-container-high text-on-surface-variant hover:text-destructive"
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        )}
        {isAvailable ? (
          <span className="material-symbols-outlined text-on-surface-variant group-hover:text-primary-text transition-colors text-xl">
            chevron_right
          </span>
        ) : !isBotPresent ? (
          <span className="material-symbols-outlined text-on-surface-variant text-xl">
            lock
          </span>
        ) : (
          <span className="material-symbols-outlined text-on-surface-variant text-xl">
            block
          </span>
        )}
      </div>
    </div>
  );
};
